import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import ClickAwayListener from '../../components/ClickAwayListener';

const Select = ({
  options = [],
  value = null,
  onChange,
  placeholder = 'Select an option',
  className = '',
  isDisabled = false,
  components = {},
  styles = {},
  menuPlacement = 'bottom',
  menuPortalTarget = null,
  autoFocus = false,
  openMenuOnFocus = false,
  inputValue = '',
  onInputChange = () => {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(inputValue);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const containerRef = useRef(null);

  // Handle outside clicks
  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  // Filter options based on search term
  const filteredOptions = options.filter(
    (option) => option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle option selection
  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
    onInputChange('');
  };

  // Calculate menu position
  const getMenuPosition = () => {
    if (!containerRef.current) return {};
    
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    if (menuPlacement === 'auto') {
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        return { bottom: '100%', maxHeight: `${spaceAbove - 10}px` };
      } else {
        return { top: '100%', maxHeight: `${spaceBelow - 10}px` };
      }
    } else if (menuPlacement === 'top') {
      return { bottom: '100%', maxHeight: `${spaceAbove - 10}px` };
    } else {
      return { top: '100%', maxHeight: `${spaceBelow - 10}px` };
    }
  };

  // Custom components
  const DropdownIndicator = components.DropdownIndicator || (() => (
    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
  ));

  const Menu = components.Menu || (({ children }) => (
    <div 
      ref={menuRef}
      className="absolute z-50 w-full bg-white shadow-lg rounded-md py-1 mt-1 overflow-auto"
      style={getMenuPosition()}
    >
      {children}
    </div>
  ));

  const selectedOption = options.find(option => option.value === value);

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <div 
        ref={containerRef}
        className={`relative ${className}`}
      >
        <div 
          className={`flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-md bg-white ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={() => !isDisabled && setIsOpen(!isOpen)}
          style={styles.control}
        >
          <div className="flex-grow">
            {isOpen ? (
              <input
                ref={inputRef}
                type="text"
                className="w-full outline-none bg-transparent"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  onInputChange(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder={placeholder}
                autoFocus
              />
            ) : (
              <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
                {selectedOption ? selectedOption.label : placeholder}
              </span>
            )}
          </div>
          <DropdownIndicator />
        </div>
        
        {isOpen && (
          menuPortalTarget ? (
            ReactDOM.createPortal(
              <Menu>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${option.value === value ? 'bg-blue-50 text-blue-600' : 'text-gray-900'}`}
                      onClick={() => handleSelect(option)}
                      style={styles.option}
                    >
                      {option.label}
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 text-gray-500">No options available</div>
                )}
              </Menu>,
              menuPortalTarget
            )
          ) : (
            <Menu>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${option.value === value ? 'bg-blue-50 text-blue-600' : 'text-gray-900'}`}
                    onClick={() => handleSelect(option)}
                    style={styles.option}
                  >
                    {option.label}
                  </div>
                ))
              ) : (
                <div className="px-3 py-2 text-gray-500">No options available</div>
              )}
            </Menu>
          )
        )}
      </div>
    </ClickAwayListener>
  );
};

Select.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  isDisabled: PropTypes.bool,
  components: PropTypes.object,
  styles: PropTypes.object,
  menuPlacement: PropTypes.oneOf(['bottom', 'top', 'auto']),
  menuPortalTarget: PropTypes.any,
  autoFocus: PropTypes.bool,
  openMenuOnFocus: PropTypes.bool,
  inputValue: PropTypes.string,
  onInputChange: PropTypes.func,
};

export default Select;
